import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  BOARD_MEMBER_CATEGORY,
  BOARD_MEMBER_NAME,
  BOARD_MEMBER_PROFESSION,
  CONTACT_EMAIL,
  CONTACT_NAME,
} from 'constants/formFields';
import { OPTION_CATEGORY_BOARD, OPTION_PROFESSION_BOARD } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useFieldsErrors from 'hooks/useFieldsErrors';
import i18n from 'i18n';
import { useCreateMaterialitySHBoardsMutation, useUpdateMaterialitySHBoardsMutation } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import useCreateBoardForm from './hooks/useCreateBoardForm';

import type IBoardItem from 'store/slices/materialAssessment/interfaces/IBoardItem';
import type IFieldError from 'store/types/IFieldError';
import type { TBoardCreateSchema } from 'utils/validators';
import type ICreateBoard from './interfaces/ICreateBoard';

import styles from './scss/CreateBoard.module.scss';

function CreateBoard({
  open, onClose, title, selectedItem, recordId, setBoards, currentId, setOpenModal, vcmId,
}: ICreateBoard) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [createBoard] = useCreateMaterialitySHBoardsMutation();
  const [updateBoard] = useUpdateMaterialitySHBoardsMutation();

  const { setFieldsErrors } = useFieldsErrors();
  const isLoading = false;

  const {
    control,
    handleSubmit,
    reset,
    fieldsName,
    setError,
  } = useCreateBoardForm({ isPageLoading: isLoading, selectedItem });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onAddBoard = (board: IBoardItem) => {
    if (currentId !== -1) {
      updateBoard({
        id: currentId, maId: recordId, data: board, vcmId,
      }).then((request) => {
        if ('data' in request && request.data?.data) {
          toast.success(i18n.t('notifications.updatedBoard'));
          setBoards((prev) => {
            const index = prev.findIndex((item) => item.id === currentId);
            if (index !== -1) {
              const updatedBoards = [...prev];
              updatedBoards[index] = { ...updatedBoards[index], ...request.data.data };
              return updatedBoards;
            }
            return prev;
          });
          setOpenModal(false);
        }
      }).catch((errors) => {
        setFieldsErrors<TBoardCreateSchema>({ errors, setError, fieldsName });
      });
    } else {
      createBoard({
        data: board,
        maId: recordId,
        vcmId,
      }).then(() => {
        toast.success(i18n.t('notifications.createdBoard'));
        setOpenModal(false);
      }).catch((errors) => {
        setFieldsErrors<TBoardCreateSchema>({ errors, setError, fieldsName });
      });
    }
  };

  const onCreateInvestorSubmit = handleSubmit((data) => {
    onAddBoard({
      contactEmail: data[CONTACT_EMAIL] || '',
      name: data[BOARD_MEMBER_NAME],
      contactName: data[CONTACT_NAME] || '',
      boardMemberCategory: data[BOARD_MEMBER_CATEGORY]?.value || '',
      boardMemberProfession: data[BOARD_MEMBER_PROFESSION]?.value || '',
    });
    onCancel();
  });

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={BOARD_MEMBER_NAME}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.boardMemberName')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                required
                placeholder={t('materialityAssessment.placeholder.typeHere')}
              />
            )}
          />
          <Controller
            control={control}
            name={BOARD_MEMBER_PROFESSION}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.boardMemberProfession')}
                placeholder={t('materialityAssessment.placeholder.selectProfessionOrArea')}
                options={OPTION_PROFESSION_BOARD}
                value={value?.value ? value : undefined}
                onChange={onChange}
                error={(error as IFieldError)?.value?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />

          <Controller
            control={control}
            name={BOARD_MEMBER_CATEGORY}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.boardMemberCategory')}
                placeholder={t('materialityAssessment.placeholder.selectCategory')}
                options={OPTION_CATEGORY_BOARD}
                value={value?.value ? value : undefined}
                onChange={onChange}
                error={(error as IFieldError)?.value?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={CONTACT_EMAIL}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.contactEmail')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={CONTACT_NAME}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.contactName')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                required
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onCreateInvestorSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}
export default CreateBoard;
